import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests and validate project context
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  const currentProjectId = localStorage.getItem('currentProjectId');
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    
    // Validate JWT project_id matches localStorage project_id
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const tokenProjectId = payload.project_id;
      
      // If project IDs don't match, we need to switch projects
      if (currentProjectId && tokenProjectId !== currentProjectId) {
        console.warn('JWT project_id mismatch detected. Token project:', tokenProjectId, 'Current project:', currentProjectId);
        // The switch will be handled by the ProjectContext when it detects the mismatch
      }
    } catch (error) {
      console.error('Error decoding JWT token:', error);
    }
  }
  return config;
});

// Handle token expiration and refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          // No refresh token, redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('username');
          window.location.href = '/';
          return Promise.reject(error);
        }

        // Try to refresh the token
        const response = await axios.post(`${API_URL}/api/auth/refresh`, {
          refresh_token: refreshToken,
          project_id: localStorage.getItem('currentProjectId'),
        });

        const { access_token } = response.data;
        localStorage.setItem('token', access_token);

        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('username');
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password, projectId = null) =>
    api.post('/auth/login', { username, password, project_id: projectId }),
  switchProject: (projectId) =>
    api.post('/auth/switch-project', { project_id: projectId }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Graph API
export const graphAPI = {
  getRelations: (domainFilter = '', minConnections = 0) => {
    const params = new URLSearchParams();
    if (domainFilter) params.append('domain_filter', domainFilter);
    if (minConnections > 0) params.append('min_connections', minConnections.toString());
    return api.get(`/graph/relations?${params.toString()}`);
  },
};

// Hosts API
export const hostsAPI = {
  getHosts: (page = 1, limit = 50, filters = {}, sortBy = 'url', sortOrder = 'asc') => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sort_by: sortBy,
      sort_order: sortOrder
    });
    
    if (filters.url) params.append('url', filters.url);
    if (filters.tech) params.append('tech', filters.tech);
    if (filters.ip) params.append('ip', filters.ip);
    if (filters.tag) params.append('tag', filters.tag);
    if (filters.status) params.append('status', filters.status);
    if (filters.use_regex) params.append('use_regex', 'true');
    
    return api.get(`/hosts?${params.toString()}`);
  },
  getHost: (hostId) => api.get(`/hosts/${hostId}`),
  updateHost: (hostId, payload) => api.put(`/hosts/${hostId}`, payload),
  deleteHost: (hostId) => api.delete(`/hosts/${hostId}`),
  importHosts: (hosts, dryRun = false) => api.post('/hosts/import', { hosts, dry_run: dryRun }),
  mergeHosts: (configJson, defId, runId) => api.post('/hosts/merge', { config_json: configJson, def_id: defId, run_id: runId }),
  setHostStatus: (hostId, status) => api.put(`/hosts/${hostId}/status`, { status }),
  deleteHostsByRegex: (pattern, dryRun = true, flags = 'i') =>
    api.post('/hosts/delete-by-regex', { pattern, dry_run: dryRun, flags }),
  addTags: (hostId, tags) => api.post(`/hosts/${hostId}/tags`, { tags }),
  removeTags: (hostId, tags) => api.delete(`/hosts/${hostId}/tags`, { data: { tags } }),
  bulkAddTags: (hostUrls, tags) => api.post('/hosts/bulk-tag', { host_urls: hostUrls, tags }),
  getAllLinks: (page = 1, limit = 20, filters = {}, sortBy = 'url', sortOrder = 'asc') => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sort_by: sortBy,
      sort_order: sortOrder
    });
    
    if (filters.status_code) params.append('status_code', filters.status_code);
    if (filters.size_min) params.append('size_min', filters.size_min);
    if (filters.size_max) params.append('size_max', filters.size_max);
    if (filters.tech) params.append('tech', filters.tech);
    if (filters.url) params.append('url', filters.url);
    if (filters.ip) params.append('ip', filters.ip);
    if (filters.use_regex) params.append('use_regex', 'true');
    
    return api.get(`/hosts/all-links?${params.toString()}`);
  },
  getTags: () => api.get('/hosts/tags'),
  getTechnologies: () => api.get('/hosts/technologies'),
};

// Diff API
export const diffAPI = {
  getDiff: (page = 1, limit = 50, source = '') => {
    let url = `/diff?page=${page}&limit=${limit}`;
    if (source) url += `&source=${encodeURIComponent(source)}`;
    return api.get(url);
  },
  getDiffGrouped: () => api.get('/diff/grouped'),
  getDiffIterable: () => api.get('/diff/iterable'),
  deleteDiff: (diffId) => api.delete(`/diff/${diffId}`),
  clearAllDiffs: () => api.delete('/diff/clear'),
};

// Config API
export const configAPI = {
  getConfig: () => api.get('/config'),
  updateConfig: (configData) => api.post('/config', configData),
  downloadConfig: () => api.get('/config/download'),
  clearConfigDatabase: () => api.delete('/config/clear'),
};


// Tasks API
export const tasksAPI = {
  getTasks: (page = 1, limit = 20, status = null, tool = null) => {
    let url = `/tasks?page=${page}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    if (tool) url += `&tool=${tool}`;
    return api.get(url);
  },
  getTask: (taskId) => api.get(`/tasks/${taskId}`),
  createTask: (taskData) => api.post('/tasks', taskData),
  getTaskStatus: (taskId) => api.get(`/tasks/${taskId}/status`),
  cancelTask: (taskId) => api.post(`/tasks/${taskId}/cancel`),
  deleteTask: (taskId) => api.delete(`/tasks/${taskId}`),
  getTaskResult: (resultId) => api.get(`/tasks/results/${resultId}`),
  downloadTaskResult: (resultId) => {
    return api.get(`/tasks/results/${resultId}/download`, { responseType: 'blob' });
  },
  getTasksStats: () => api.get('/tasks/stats'),
  passToTool: (taskId, targetTool, options = {}, createNewGroup = true) =>
    api.post(`/tasks/${taskId}/pass-to`, {
      tool: targetTool,
      options: options,
      create_new_group: createNewGroup,
    }),
};

// Task Groups API
export const taskGroupsAPI = {
  getGroups: (page = 1, limit = 20, status = '') => {
    let url = `/task-groups?page=${page}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    return api.get(url);
  },
  getGroup: (groupId) => api.get(`/task-groups/${groupId}`),
  createGroup: (groupData) => api.post('/task-groups', groupData),
  deleteGroup: (groupId, deleteTasks = false) =>
    api.delete(`/task-groups/${groupId}?delete_tasks=${deleteTasks}`),
  cancelGroup: (groupId) => api.post(`/task-groups/${groupId}/cancel`),
  downloadArchive: (groupId) => api.get(`/task-groups/${groupId}/download-archive`, { responseType: 'blob' })
};

// Tools API
export const toolsAPI = {
  getCompatibility: () => api.get('/tools/compatibility'),
  getList: () => api.get('/tools/list'),
};

// Input Sources API
export const inputSourcesAPI = {
  getInputSources: () => api.get('/input-sources'),
  getConfigData: (type, tags = '') => {
    let url = `/input-sources/config-data?type=${type}`;
    if (tags) url += `&tags=${encodeURIComponent(tags)}`;
    return api.get(url);
  },
  getMongoCollections: () => api.get('/input-sources/mongo/collections'),
  executeMongoQuery: (payload) => api.post('/input-sources/mongo/query', payload),
};

// Vulnerabilities API
export const vulnerabilitiesAPI = {
  getVulnerabilities: (page = 1, limit = 50, filters = {}, sort = {}) => {
    let url = `/vulnerabilities?page=${page}&limit=${limit}`;
    if (filters.severity) url += `&severity=${filters.severity}`;
    if (filters.status) url += `&status=${filters.status}`;
    if (filters.host) url += `&host=${encodeURIComponent(filters.host)}`;
    if (filters.researcher) url += `&researcher=${encodeURIComponent(filters.researcher)}`;
    if (filters.attention) url += `&attention=${filters.attention}`;
    if (filters.overdue) url += `&overdue=true`;
    if (sort.by) url += `&sort_by=${sort.by}`;
    if (sort.dir) url += `&sort_dir=${sort.dir}`;
    return api.get(url);
  },
  listResearchers: () => api.get('/vulnerabilities/researchers'),
  getMyAttention: () => api.get('/vulnerabilities/my-attention'),
  getVulnerability: (vulnId) => api.get(`/vulnerabilities/${vulnId}`),
  createVulnerability: (vulnData) => api.post('/vulnerabilities', vulnData),
  updateVulnerability: (vulnId, vulnData) => api.put(`/vulnerabilities/${vulnId}`, vulnData),
  deleteVulnerability: (vulnId) => api.delete(`/vulnerabilities/${vulnId}`),
  getStats: () => api.get('/vulnerabilities/stats'),
  exportFromNuclei: (resultId) => api.post(`/tasks/results/${resultId}/export-to-vulnerabilities`),
  importVulnerabilities: (vulnerabilities, dryRun = false) => api.post('/vulnerabilities/import', { vulnerabilities, dry_run: dryRun }),
  // Pyrus Sync
  getPyrusSyncConfig: () => api.get('/vulnerabilities/pyrus-sync/config'),
  updatePyrusSyncConfig: (data) => api.put('/vulnerabilities/pyrus-sync/config', data),
  runPyrusSyncNow: () => api.post('/vulnerabilities/pyrus-sync/run-now'),
  listPyrusSyncRuns: (page = 1, limit = 10) => api.get(`/vulnerabilities/pyrus-sync/runs?page=${page}&limit=${limit}`),
};

// Projects API
export const projectsAPI = {
  getProjects: () => api.get('/projects'),
  getProject: (projectId) => api.get(`/projects/${projectId}`),
  createProject: (data) => api.post('/projects', data),
  updateProject: (projectId, data) => api.put(`/projects/${projectId}`, data),
  deleteProject: (projectId) => api.delete(`/projects/${projectId}`),
  createInvite: (projectId) => api.post(`/projects/${projectId}/invite`),
  acceptInvite: (inviteCode) => api.post(`/invites/${inviteCode}/accept`),
  getMembers: (projectId) => api.get(`/projects/${projectId}/members`),
  removeMember: (projectId, userId) => api.delete(`/projects/${projectId}/members/${userId}`),
  exportProject: (projectId) => api.get(`/projects/${projectId}/export`, { responseType: 'blob' }),
  importProject: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/projects/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Users API
export const usersAPI = {
  createUser: (data) => api.post('/users', data)
};

// Scan Templates API
export const scanTemplatesAPI = {
  getScanTemplates: () => api.get('/scan-templates'),
  getScanTemplate: (templateId) => api.get(`/scan-templates/${templateId}`),
  createScanTemplate: (data) => api.post('/scan-templates', data),
  updateScanTemplate: (templateId, data) => api.put(`/scan-templates/${templateId}`, data),
  deleteScanTemplate: (templateId) => api.delete(`/scan-templates/${templateId}`)
};

// Comments API
export const commentsAPI = {
  list: ({ scope, hostId = null, page = 1, limit = 20 }) => {
    let url = `/comments?scope=${encodeURIComponent(scope)}&page=${page}&limit=${limit}`;
    if (scope === 'host' && hostId) url += `&host_id=${encodeURIComponent(hostId)}`;
    return api.get(url);
  },
  create: ({ scope, body_md, host_id = null }) => api.post('/comments', { scope, body_md, host_id }),
  update: (id, { body_md }) => api.put(`/comments/${id}`, { body_md }),
  remove: (id) => api.delete(`/comments/${id}`),
};

// Wiki API
export const wikiAPI = {
  getTree: (scope = 'project') => api.get(`/comments/tree?scope=${encodeURIComponent(scope)}`),
  getPage: (pageId) => api.get(`/comments/${pageId}`),
  createSection: (data) => api.post('/comments', { ...data, type: 'section', scope: 'project' }),
  createPage: (data) => api.post('/comments', { ...data, type: 'page', scope: 'project' }),
  updatePage: (id, data) => api.put(`/comments/${id}`, data),
  deletePage: (id) => api.delete(`/comments/${id}`),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/wiki/images/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getImageUrl: (imageId) => `${API_URL}/api/wiki/images/${imageId}`
};

// AutoDiscovery API
export const autodiscoveryAPI = {
  listDefs: () => api.get('/autodiscovery/defs'),
  createDef: (payload) => api.post('/autodiscovery/defs', payload),
  updateDef: (id, payload) => api.put(`/autodiscovery/defs/${id}`, payload),
  deleteDef: (id) => api.delete(`/autodiscovery/defs/${id}`),
  enableDef: (id) => api.post(`/autodiscovery/defs/${id}/enable`),
  disableDef: (id) => api.post(`/autodiscovery/defs/${id}/disable`),
  runNow: (id) => api.post(`/autodiscovery/defs/${id}/run-now`),
  listRuns: ({ defId = null, status = null, page = 1, limit = 20 }) => {
    let url = `/autodiscovery/runs?page=${page}&limit=${limit}`;
    if (defId) url += `&def_id=${encodeURIComponent(defId)}`;
    if (status) url += `&status=${encodeURIComponent(status)}`;
    return api.get(url);
  },
  cancelRun: (runId) => api.post(`/autodiscovery/runs/${runId}/cancel`),
  getClassificationResults: (runId) => api.get(`/autodiscovery/runs/${runId}/classification`),
  getRunConfigJson: (runId) => api.get(`/autodiscovery/runs/${runId}/config-json`),
  getDefaultPrompt: () => api.get('/autodiscovery/prompt/default'),
  getRunProgress: (runId) => api.get(`/autodiscovery/runs/${runId}/progress`),
};

// Healthcheck API
export const healthcheckAPI = {
  getConfig: () => api.get('/healthcheck/config'),
  updateConfig: (payload) => api.put('/healthcheck/config', payload),
  runNow: () => api.post('/healthcheck/run-now'),
  listRuns: (page = 1, limit = 20) => api.get(`/healthcheck/runs?page=${page}&limit=${limit}`),
  cancelRun: (runId) => api.post(`/healthcheck/runs/${runId}/cancel`),
  getStatus: () => api.get('/healthcheck/status'),
  deleteByStatus: (status) => api.post('/hosts/delete-by-status', { status }),
  getHostHistory: (hostName) => api.get(`/hosts/${encodeURIComponent(hostName)}/healthcheck-history`),
  getStatusChanges: (page = 1, limit = 50) => api.get(`/healthcheck/status-changes?page=${page}&limit=${limit}`),
  getHostsByStatus: (status) => api.get(`/healthcheck/hosts-by-status?status=${status}`),
  deleteSelectedHosts: (hostIds) => api.post('/hosts/delete-selected', { host_ids: hostIds }),
  getRunChanges: (runId) => api.get(`/healthcheck/runs/${runId}/changes`),
};

// Classifications API
export const classificationAPI = {
  getLatestByHost: (host) => api.get(`/classifications?host=${encodeURIComponent(host)}`),
  list: (page = 1, limit = 20) => api.get(`/classifications?page=${page}&limit=${limit}`),
};

// Files API
export const filesAPI = {
  list: (path = '') => {
    const url = path ? `/files/list?path=${encodeURIComponent(path)}` : '/files/list';
    return api.get(url);
  },
  createDirectory: (path) => api.post('/files/directory', { path }),
  delete: (path) => api.delete(`/files?path=${encodeURIComponent(path)}`),
  upload: (file, path = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path);
    return api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  download: (path) => {
    return api.get(`/files/download?path=${encodeURIComponent(path)}`, { responseType: 'blob' });
  },
  uploadArchive: (file, extractPath = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', extractPath);
    return api.post('/files/archive/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadArchive: (path) => {
    return api.get(`/files/archive/download?path=${encodeURIComponent(path)}`, { responseType: 'blob' });
  },
  readFile: (path) => {
    return api.get(`/files/read?path=${encodeURIComponent(path)}`);
  },
  saveFile: (path, content) => {
    return api.post('/files/save', { path, content });
  },
  moveFile: (sourcePath, targetPath) => {
    return api.post('/files/move', { source_path: sourcePath, target_path: targetPath });
  },
};

// Data Feeds API
export const dataFeedsAPI = {
  list: () => api.get('/data-feeds'),
  get: (feedId) => api.get(`/data-feeds/${feedId}`),
  create: (data) => api.post('/data-feeds', data),
  update: (feedId, data) => api.put(`/data-feeds/${feedId}`, data),
  delete: (feedId) => api.delete(`/data-feeds/${feedId}`),
  regenerateKey: (feedId) => api.post(`/data-feeds/${feedId}/regenerate-key`),
  preview: (feedId) => api.post(`/data-feeds/${feedId}/preview`),
};

export default api;
